<template>
  <div class="columns-container">
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Exterior') }}</div>
        </template>
        <template #body>
          <configuration-feature
            :feature="originalColor"
            :feature-label="i18n.t('/Configuration/Color')"
          />
          <configuration-feature
            :feature="originalRim"
            :feature-label="i18n.t('/Configuration/Rims')"
          />
        </template>
      </quote-list-block>
    </div>
    <div class="column">
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Exterior') }}</div>
        </template>
        <template #body>
          <configuration-feature
            :can-detect-feature-change="true"
            :original-feature="originalColor"
            :feature="editableColor"
            :feature-label="i18n.t('/Configuration/Color')"
            :can-change="canChangeColor"
            @feature-edit-requested="handleColorChanged"
            @feature-changed="handleColorChanged"
          />
          <configuration-feature
            :can-detect-feature-change="true"
            :original-feature="originalRim"
            :feature="editableRim"
            :feature-label="i18n.t('/Configuration/Rims')"
            :can-change="canChangeRim"
            @feature-edit-requested="handleRimChanged"
            @feature-changed="handleRimChanged"
          />
        </template>
      </quote-list-block>
    </div>

    <single-feature-change-dialog
      v-if="showColorChangeDialog"
      :show-options-search-input="true"
      :title="i18n.t('/change-order/configuration/change-color')"
      :items="availableColors"
      @close="showColorChangeDialog = false"
      @confirm="confirmColorChange"
    />
    <single-feature-change-dialog
      v-if="showRimChangeDialog"
      :show-options-search-input="true"
      :title="i18n.t('/change-order/configuration/change-rims')"
      :items="availableRims"
      @close="showRimChangeDialog = false"
      @confirm="confirmRimChange"
    />
  </div>
</template>

<script>
import QuoteListBlock from '@/components/generic/presentational/molecules/quoteListBlock/QuoteListBlock.vue'
import ConfigurationFeature from '@/components/changeOrder/presentational/organisms/configurationFeature/ConfigurationFeature.vue'
import SingleFeatureChangeDialog from '@/components/changeOrder/presentational/organisms/singleFeatureChangeDialog/singleFeatureChangeDialog.vue'
import { mapGetters } from 'vuex'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'

export default {
  components: {
    SingleFeatureChangeDialog,
    ConfigurationFeature,
    QuoteListBlock,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showColorChangeDialog: false,
      showRimChangeDialog: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
    }),
    originalColor() {
      let options = this.changeOrder.CarConfiguration.OriginalOptions
      return options.find((option) => option.Type === 'EXTERIOR_COLOR')
    },
    editableColor() {
      let options = this.changeOrder.CarConfiguration.ChangedOptions
      return options.find((option) => option.Type === 'EXTERIOR_COLOR')
    },
    availableColors() {
      let availableOptions = this.changeOrder.CarConfiguration.AvailableOptions
      let filteredOptions = availableOptions.filter((option) => option.Type === 'EXTERIOR_COLOR')
      return this.sortOptionsBy(filteredOptions, 'shortId')
    },
    originalRim() {
      let options = this.changeOrder.CarConfiguration.OriginalOptions
      // Mixed packages selected as option live in the options list, not in the rim slot
      return options.find((option) => option.Type === 'EXTERIOR_RIMS' && !option.MixedPackage)
    },
    editableRim() {
      let options = this.changeOrder.CarConfiguration.ChangedOptions
      return options.find((option) => option.Type === 'EXTERIOR_RIMS' && !option.MixedPackage)
    },
    availableRims() {
      let availableOptions = this.changeOrder.CarConfiguration.AvailableOptions
      let filteredOptions = availableOptions.filter((option) => option.Type === 'EXTERIOR_RIMS')

      // A mixed package on the car as normal option already replaces the rims:
      // standard rims can then no longer be selected
      let mixedPackageOption = this.mixedPackageOnCarAsOption
      if (mixedPackageOption) {
        filteredOptions = filteredOptions.map((option) =>
          option.Classification === 'STANDARD_EQUIPMENT'
            ? {
                ...option,
                Disabled: true,
                DisabledReason: `Niet mogelijk in combinatie met optie ${mixedPackageOption.ShortId}`,
              }
            : option
        )
      }

      return this.sortOptionsBy(filteredOptions, 'shortId')
    },
    mixedPackageOnCarAsOption() {
      let mixedPackageIds = this.changeOrder.CarConfiguration.AvailableOptions.filter(
        (option) => option.MixedPackage
      ).map((option) => option.Id)

      // In the options list means: any entry except the actual rim slot entry
      return this.changeOrder.CarConfiguration.ChangedOptions.find(
        (option) =>
          mixedPackageIds.includes(option.Id) &&
          !(option.Type === 'EXTERIOR_RIMS' && !option.MixedPackage)
      )
    },
    canChangeColor() {
      return this.getChangePermission(this.changeOrder.Permissions.CanChangeColor)
    },
    canChangeRim() {
      return this.getChangePermission(this.changeOrder.Permissions.CanChangeFactoryOption)
    },
  },
  methods: {
    getChangePermission(basePermission) {
      const status = this.changeOrder.MainOrderStatus
      if (status === ChangeOrderStatus.submitted || status === ChangeOrderStatus.archived) {
        return {
          Allowed: false,
          Reason: 'Order has been submitted or deleted',
        }
      }

      if (this.changeOrder.CarConfiguration.HasUnknownOkapiModel) {
        return {
          Allowed: false,
          Reason:
            'Het is voor dit voertuig niet mogelijk om configuratiewijzigingen door te voeren. ' +
            'Neem contact op met DSS',
        }
      }

      return basePermission
    },
    paddedId(id) {
      let idStr = String(id)
      while (idStr.length < this.maxIdLength) {
        idStr += '\xa0' // this is a non-breaking space
      }
      return idStr
    },
    handleColorChanged() {
      this.showColorChangeDialog = true
    },
    confirmColorChange(selectedColor) {
      let changedColorOptions = this.changeOrder.CarConfiguration.ChangedOptions.filter(
        (option) => option.Type !== 'EXTERIOR_COLOR'
      )

      let selectedColorOption = this.changeOrder.CarConfiguration.AvailableOptions.find(
        (option) => option.Id === selectedColor.Id
      )

      changedColorOptions.push(selectedColorOption)

      this.changeOrder.CarConfiguration.ChangedOptions = changedColorOptions

      this.$store.commit('changeOrder/setCarConfigurationStatus')
      this.showColorChangeDialog = false
    },
    handleRimChanged() {
      this.showRimChangeDialog = true
    },
    confirmRimChange(selectedRim) {
      // Remove the current rim (mixed packages in the options list keep their rim
      // type but stay untouched) and the selected rim itself when it is currently
      // in the options list: it can only be on the car once
      let changedRimOptions = this.changeOrder.CarConfiguration.ChangedOptions.filter(
        (option) =>
          (option.Type !== 'EXTERIOR_RIMS' || option.MixedPackage) && option.Id !== selectedRim.Id
      )

      let selectedRimOption = this.changeOrder.CarConfiguration.AvailableOptions.find(
        (option) => option.Id === selectedRim.Id
      )

      // Once selected as rim it is a plain rim: clear MixedPackage so it only
      // shows in the rim slot and no longer in the options list
      changedRimOptions.push({ ...selectedRimOption, MixedPackage: false })

      this.changeOrder.CarConfiguration.ChangedOptions = changedRimOptions

      this.$store.commit('changeOrder/setCarConfigurationStatus')
      this.showRimChangeDialog = false
    },
    sortOptionsBy(options, key) {
      return options.sort((a, b) => {
        let valA = a[key]
        let valB = b[key]
        if (valA < valB) {
          return -1
        }
        if (valA > valB) {
          return 1
        }
        return 0
      })
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.row) {
  grid-template-columns: auto 4em 8em 1em 2em;
  grid-template-areas: 'title code price-incl info-icon edit-dialog';
}

.info-icon {
  cursor: pointer;
}

.columns-container {
  display: flex;
  justify-content: space-between;
}

.column {
  flex: 1;
  margin: 0 10px;
}
</style>
